import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import Loading from 'src/components/Loading';
import { TariffInfo } from 'src/components/Tariff';
import { activeTariffs } from 'src/util/meterTariff';

/**
 * Renders a card to inform the user that there are no active tariffs for this meter.
 * @returns {React.ReactElement} The card component.
 */
export function NoTariffsCard() {
  const intl = useIntl();
  const noTariffsMessage = intl.formatMessage({
    id: 'property.property_settings.property_settings_meter_tariff.no_tariffs.message',
    defaultMessage: 'No active tariffs for this meter.',
  });

  return (
    <div className="p-3 card">
      {noTariffsMessage}
    </div>
  );
}

const PropertySettingsMeterTariffFragment = graphql`
  fragment PropertySettingsMeterTariff_property on Property @throwOnFieldError {
    meters(first:500) {
      edges {
        node {
          uuid
          identifier
          title
          tariffs(first:500) {
            edges {
              node {
                uuid
                identifier
                tariffType
                title
                active { start finish }
                ...TariffInfo_tariff
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * @typedef {object} TimeRange
 * @property {number} start - Unix timestamp for start date (required).
 * @property {number|null} finish - Unix timestamp for end date (null if ongoing).
 */

/**
 * @typedef {object} Tariff
 * @property {string} uuid - Unique identifier for the tariff.
 * @property {string} identifier - External identifier for the tariff.
 * @property {string} tariffType - Type of tariff (TARIFF_TYPE_ENERGY, TARIFF_TYPE_NETWORK, etc.).
 * @property {string|null} title - Display name of the tariff.
 * @property {TimeRange} active - Active time range for the tariff.
 */

/**
 * @typedef {object} TariffConnection
 * @property {Array<{node: Tariff}>} edges - Array of tariff edges from GraphQL connection.
 */

/**
 * @typedef {object} Meter
 * @property {string} uuid - Unique identifier for the meter.
 * @property {string} identifier - External identifier for the meter.
 * @property {string} title - Display name of the meter (required in schema).
 * @property {TariffConnection|null} tariffs - GraphQL connection to associated tariffs.
 */

/**
 * @typedef {object} MeterConnection
 * @property {Array<{node: Meter}>} edges - Array of meter edges from GraphQL connection.
 */

/**
 * @typedef {object} Property
 * @property {MeterConnection} meters - GraphQL connection containing meter data with associated
 *  tariffs.
 */

/**
 * PropertySettingsMeterTariff component displays tariffs for each meter in a property.
 * Shows active tariffs for each meter or a user-friendly message when no tariffs are configured.
 * @param {object} props - The component's props.
 * @param {Property} props.property - Property data containing meters and their tariffs from the
 *  PropertySettingsMeterTariffFragment GraphQL fragment.
 * @returns {React.ReactElement} The rendered component showing meter tariffs or loading/empty
 *  states.
 */
function PropertySettingsMeterTariff(props) {
  if (!props || Object.keys(props).length === 0) {
    return <Loading />;
  }

  let { property } = props;

  // Handle null property case before calling useFragment
  if (!property) {
    return <div className="property-active-tariffs" />;
  }

  property = useFragment(PropertySettingsMeterTariffFragment, property);
  const { meters } = property || {};

  // Handle case where property or meters is null/undefined
  if (!meters?.edges) {
    return <div className="property-active-tariffs" />;
  }

  return (
    <div className="mb-4 property-active-tariffs">
      {meters.edges.map((el) => el.node).map((meter) => {
        const meterActiveTariffs = meter.tariffs && meter.tariffs.edges
          ? activeTariffs(meter.tariffs.edges)
          : [];

        return (
          <Fragment key={meter.uuid}>
            <hr />
            <div>
              <h3>
                {meter.title}
                <br />
                <small>{meter.identifier}</small>
              </h3>
              {meterActiveTariffs && meterActiveTariffs.length > 0 ? (
                meterActiveTariffs.map((tariff) => (
                  <TariffInfo tariff={tariff} key={tariff.uuid} />
                ))
              ) : (
                <NoTariffsCard />
              )}
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}

export default PropertySettingsMeterTariff;

PropertySettingsMeterTariff.propTypes = {
  property: PropTypes.shape({
    meters: PropTypes.shape({
      edges: PropTypes.arrayOf(
        PropTypes.shape({
          node: PropTypes.shape({
            uuid: PropTypes.string.isRequired,
            identifier: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
            tariffs: PropTypes.shape({
              edges: PropTypes.arrayOf(
                PropTypes.shape({
                  node: PropTypes.shape({
                    uuid: PropTypes.string.isRequired,
                    identifier: PropTypes.string.isRequired,
                    tariffType: PropTypes.string.isRequired,
                    title: PropTypes.string,
                    active: PropTypes.shape({
                      start: PropTypes.number.isRequired,
                      finish: PropTypes.number,
                    }).isRequired,
                  }).isRequired,
                }).isRequired,
              ),
            }),
          }).isRequired,
        }).isRequired,
      ).isRequired,
    }).isRequired,
  }).isRequired,
};
